<template>
  <div class="reset-password-page">
    <EsHeaderView />
    <div class="reset-container app-container">
      <h2 class="title">{{ $t('message.home.resetPassword') || 'Reset Password' }}</h2>

      <!-- Step 1: 输入邮箱发送验证码 -->
      <div v-if="step === 1">
        <el-form :model="form" :rules="rules1" ref="step1Form" label-position="top">
          <el-form-item :label="$t('message.home.email') || 'Email'" prop="email">
            <el-input v-model="form.email" placeholder="example@email.com"></el-input>
          </el-form-item>
          <el-form-item>
            <el-button type="primary" :loading="sendLoading" @click="sendCode" class="full-btn">
              {{ $t('message.home.sendVerifyCode') || 'Send Verification Code' }}
            </el-button>
          </el-form-item>
        </el-form>
      </div>

      <!-- Step 2: 输入验证码和新密码 -->
      <div v-if="step === 2">
        <p class="sent-hint">
          {{ $t('message.home.codeSentTo') || 'Verification code sent to' }}: <strong>{{ form.email }}</strong>
        </p>
        <el-form :model="form" :rules="rules2" ref="step2Form" label-position="top">
          <el-form-item :label="$t('message.home.verificationCode') || 'Verification Code'" prop="code">
            <el-input v-model="form.code" :placeholder="$t('message.home.enterCode') || 'Enter 6-digit code'" maxlength="6"></el-input>
          </el-form-item>
          <el-form-item :label="$t('message.home.newPassword') || 'New Password'" prop="newPassword">
            <el-input v-model="form.newPassword" type="password" show-password
              :placeholder="$t('message.home.enterNewPassword') || 'Enter new password'"></el-input>
          </el-form-item>
          <el-form-item :label="$t('message.home.confirmPassword') || 'Confirm Password'" prop="confirmPassword">
            <el-input v-model="form.confirmPassword" type="password" show-password
              :placeholder="$t('message.home.confirmNewPassword') || 'Confirm new password'"></el-input>
          </el-form-item>
          <el-form-item>
            <el-button type="primary" :loading="resetLoading" @click="doReset" class="full-btn">
              {{ $t('message.home.resetPassword') || 'Reset Password' }}
            </el-button>
          </el-form-item>
          <el-form-item>
            <el-button @click="sendCode" :disabled="countdown > 0" class="full-btn">
              {{ countdown > 0 ? `${$t('message.home.resend') || 'Resend'} (${countdown}s)` : ($t('message.home.resendCode') || 'Resend Code') }}
            </el-button>
          </el-form-item>
        </el-form>
      </div>

      <!-- Step 3: 成功 -->
      <div v-if="step === 3" class="success-box">
        <i class="el-icon-success success-icon"></i>
        <h3>{{ $t('message.home.passwordResetSuccess') || 'Password reset successfully!' }}</h3>
        <el-button type="primary" @click="goLogin" class="full-btn" style="margin-top: 20px;">
          {{ $t('message.home.goLogin') || 'Go to Login' }}
        </el-button>
      </div>
    </div>
  </div>
</template>

<script>
import { sendVerifyCode, resetPassword } from '@/api/upms/guest'

export default {
  name: 'ResetPasswordPage',
  data() {
    const validateConfirmPassword = (rule, value, callback) => {
      if (value !== this.form.newPassword) {
        callback(new Error(this.$t('message.home.passwordNotMatch') || 'Passwords do not match'))
      } else {
        callback()
      }
    }
    return {
      step: 1,
      sendLoading: false,
      resetLoading: false,
      countdown: 0,
      countdownTimer: null,
      form: {
        email: '',
        code: '',
        newPassword: '',
        confirmPassword: ''
      },
      rules1: {
        email: [
          { required: true, message: this.$t('message.home.emailRequired') || 'Email is required', trigger: 'blur' },
          { type: 'email', message: this.$t('message.home.emailInvalid') || 'Invalid email format', trigger: 'blur' }
        ]
      },
      rules2: {
        code: [
          { required: true, message: this.$t('message.home.codeRequired') || 'Verification code is required', trigger: 'blur' },
          { len: 6, message: this.$t('message.home.codeLength') || 'Code must be 6 digits', trigger: 'blur' }
        ],
        newPassword: [
          { required: true, message: this.$t('message.home.passwordRequired') || 'Password is required', trigger: 'blur' },
          { min: 6, message: this.$t('message.home.passwordMinLength') || 'Password must be at least 6 characters', trigger: 'blur' }
        ],
        confirmPassword: [
          { required: true, message: this.$t('message.home.confirmPasswordRequired') || 'Please confirm password', trigger: 'blur' },
          { validator: validateConfirmPassword, trigger: 'blur' }
        ]
      }
    }
  },
  beforeDestroy() {
    if (this.countdownTimer) {
      clearInterval(this.countdownTimer)
    }
  },
  methods: {
    sendCode() {
      const doSend = async () => {
        this.sendLoading = true
        try {
          await sendVerifyCode({ email: this.form.email })
          this.$message.success(this.$t('message.home.codeSent') || 'Verification code sent')
          this.step = 2
          this.startCountdown()
        } catch (error) {
          this.$message.error(error.message || this.$t('message.home.sendFailed') || 'Failed to send code')
        } finally {
          this.sendLoading = false
        }
      }

      if (this.step === 1) {
        this.$refs.step1Form.validate((valid) => {
          if (valid) doSend()
        })
      } else {
        doSend()
      }
    },

    startCountdown() {
      this.countdown = 60
      if (this.countdownTimer) clearInterval(this.countdownTimer)
      this.countdownTimer = setInterval(() => {
        this.countdown--
        if (this.countdown <= 0) {
          clearInterval(this.countdownTimer)
        }
      }, 1000)
    },

    doReset() {
      this.$refs.step2Form.validate(async (valid) => {
        if (!valid) return
        this.resetLoading = true
        try {
          await resetPassword({
            email: this.form.email,
            code: this.form.code,
            newPassword: this.form.newPassword
          })
          this.step = 3
        } catch (error) {
          this.$message.error(error.message || this.$t('message.home.resetFailed') || 'Reset failed')
        } finally {
          this.resetLoading = false
        }
      })
    },

    goLogin() {
      this.$router.push('/login')
    }
  }
}
</script>

<style scoped lang="scss">
.reset-password-page {
  min-height: 100vh;
  background: #f5f5f5;
}

.reset-container {
  max-width: 480px;
  margin: 40px auto;
  padding: 30px;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);

  .title {
    text-align: center;
    margin-bottom: 30px;
    font-size: 22px;
  }

  .sent-hint {
    margin-bottom: 20px;
    color: #666;
    font-size: 14px;
  }

  .full-btn {
    width: 100%;
    height: 42px;
  }

  .success-box {
    text-align: center;
    padding: 40px 0;

    .success-icon {
      font-size: 60px;
      color: #67c23a;
    }

    h3 {
      margin-top: 15px;
      font-size: 18px;
      color: #333;
    }
  }
}
</style>
